import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, isoJour, montant } from '../lib/format';
import type { BilanEntretiens, Vehicule } from '../api/types';

interface Entretien {
  _id: string; type: string; date: string; km: number; coutMinor: number;
  prestataire?: string; description?: string;
  immobilisationDebut?: string; immobilisationFin?: string;
  prochaineEcheanceKm?: number; prochaineEcheanceDate?: string;
  vehicle?: { _id: string; marque: string; modele: string; immatriculation: string };
}

export default function Entretien() {
  const { peut } = useAuth();
  const [liste, setListe] = useState<Entretien[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [bilan, setBilan] = useState<BilanEntretiens | null>(null);
  const [vueBilan, setVueBilan] = useState(false);
  const [f, setF] = useState({
    vehicle: '', type: 'Vidange', date: isoJour(new Date()), km: '', cout: '',
    prestataire: '', description: '', immoDebut: '', immoFin: '', prochainKm: '',
  });
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => { api.get<Entretien[]>('/operations/entretiens').then(setListe); }, []);
  useEffect(() => { charger(); api.get<Vehicule[]>('/vehicules').then(setVehicules); }, [charger]);
  if (!liste) return <Squelette lignes={6} />;

  async function creer() {
    setErreur('');
    try {
      await api.post('/operations/entretiens', {
        vehicle: f.vehicle, type: f.type, date: f.date,
        km: Number(f.km || 0), coutMinor: centimes(Number(f.cout || 0)),
        prestataire: f.prestataire || undefined, description: f.description || undefined,
        immobilisationDebut: f.immoDebut || undefined, immobilisationFin: f.immoFin || undefined,
        prochaineEcheanceKm: f.prochainKm ? Number(f.prochainKm) : undefined,
      });
      setOuvert(false); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const total = liste.reduce((a, e) => a + e.coutMinor, 0);
  const aVenir = vehicules
    .map((v) => ({ v, dernier: liste.find((e) => e.vehicle?._id === v._id) }))
    .filter((x) => x.dernier?.prochaineEcheanceKm && v_reste(x.v.km, x.dernier.prochaineEcheanceKm) < 2000);

  function v_reste(km: number, echeance: number) { return echeance - km; }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Entretien</h1>
          <p>{liste.length} intervention(s) · {montant(total)} dépenses</p>
        </div>
        <div className="actions">
          <button className={`btn ${vueBilan ? 'btn-p' : ''}`}
                  onClick={() => { setVueBilan(!vueBilan); if (!bilan) void api.get<BilanEntretiens>('/operations/entretiens/bilan').then(setBilan).catch(() => {}); }}>
            {vueBilan ? 'Voir les interventions' : 'Voir le bilan'}
          </button>
          {peut('modifier.vehicule') && (
            <button className="btn btn-p" onClick={() => setOuvert(true)}>Enregistrer un entretien</button>
          )}
        </div>
      </div>

      {/*
        * Le bilan, pas la liste.
        *
        * La question n'est pas « qu'a-t-on fait sur la Clio » mais « quelle
        * voiture me coute le plus cher, et combien de jours m'a-t-elle fait
        * perdre ». Une voiture au garage ne loue pas : le manque a gagner est
        * la moitie invisible de la facture, et il depasse souvent la facture.
        */}
      {vueBilan && (
        bilan === null ? <Squelette lignes={5} /> : (
          <>
            <div className="grille g4" style={{ marginBottom: 16 }}>
              <div className="carte carte-p">
                <div className="micro">Dépensé en entretien</div>
                <div className="v montant">{montant(bilan.totalCoutMinor)}</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">Jours d'immobilisation</div>
                <div className="v">{bilan.totalJoursImmobilises}</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">Manque à gagner</div>
                <div className="v montant" style={{ color: 'var(--danger)' }}>{montant(bilan.totalManqueAGagnerMinor)}</div>
                <div className="mini sourdine">ces jours-là, la voiture ne pouvait pas être louée</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">Coût réel</div>
                <div className="v montant">{montant(bilan.totalCoutMinor + bilan.totalManqueAGagnerMinor)}</div>
                <div className="mini sourdine">facture + immobilisation</div>
              </div>
            </div>

            <Carte pad={false} titre="Par voiture">
              <div className="enveloppe-tbl">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Véhicule</th><th className="droite">Interventions</th>
                      <th className="droite">Dépensé</th><th className="droite">Jours immobilisée</th>
                      <th className="droite">Manque à gagner</th><th className="droite">Coût / 1 000 km</th>
                      <th>Prochaine échéance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilan.lignes.filter((l) => l.nbEntretiens > 0).map((l) => (
                      <tr key={l.vehicleId}>
                        <td><b>{l.libelle}</b><div className="mini sourdine">{l.immatriculation} · {l.km.toLocaleString('fr-FR')} km</div></td>
                        <td className="droite num">{l.nbEntretiens}</td>
                        <td className="droite montant">{montant(l.coutTotalMinor)}</td>
                        <td className="droite num">{l.joursImmobilises}</td>
                        <td className="droite montant" style={{ color: 'var(--danger)' }}>{montant(l.manqueAGagnerMinor)}</td>
                        <td className="droite montant">{montant(l.coutParKmMinor)}</td>
                        <td className="mini" style={{ color: l.prochaine?.includes('dépassée') ? 'var(--danger)' : 'var(--texte2)' }}>
                          {l.prochaine ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Carte>
            <p className="mini sourdine" style={{ marginTop: 12 }}>
              Le manque à gagner est calculé au tarif de la voiture : une voiture au garage ne loue pas.
              Le coût par 1 000 km permet de comparer des voitures qui ne roulent pas autant.
            </p>
          </>
        )
      )}

      {!vueBilan && aVenir.length > 0 && (
        <div style={{ marginBottom: 14, display: 'grid', gap: 8 }}>
          {aVenir.map((x) => (
            <Bandeau key={x.v._id} type="warn" titre={`${x.v.marque} ${x.v.modele} — ${x.v.immatriculation}`}>
              Prochaine échéance a {x.dernier!.prochaineEcheanceKm!.toLocaleString('fr-FR')} km,
              le compteur affiche {x.v.km.toLocaleString('fr-FR')} km — il reste {v_reste(x.v.km, x.dernier!.prochaineEcheanceKm!).toLocaleString('fr-FR')} km.
            </Bandeau>
          ))}
        </div>
      )}

      {!vueBilan && (
      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre="Aucun entretien enregistré" /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>Date</th><th>Véhicule</th><th>Intervention</th><th>Prestataire</th>
                    <th className="droite">Km</th><th className="droite">Coût</th><th>Immobilisation</th><th className="droite">Prochaine échéance</th></tr>
              </thead>
              <tbody>
                {liste.map((e) => (
                  <tr key={e._id}>
                    <td className="num">{dateFr(e.date)}</td>
                    <td><b>{e.vehicle?.marque} {e.vehicle?.modele}</b><div className="mini sourdine">{e.vehicle?.immatriculation}</div></td>
                    <td>{e.type}{e.description && <div className="mini sourdine">{e.description}</div>}</td>
                    <td className="mini sourdine">{e.prestataire ?? '—'}</td>
                    <td className="droite num">{e.km.toLocaleString('fr-FR')}</td>
                    <td className="droite montant">{montant(e.coutMinor)}</td>
                    <td className="mini sourdine">{e.immobilisationDebut ? `${dateFr(e.immobilisationDebut)} → ${dateFr(e.immobilisationFin)}` : '—'}</td>
                    <td className="droite num mini">{e.prochaineEcheanceKm ? `${e.prochaineEcheanceKm.toLocaleString('fr-FR')} km` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>
      )}

      {!vueBilan && (
      <p className="mini sourdine" style={{ marginTop: 12 }}>
        Une immobilisation renseignée bloque automatiquement le véhicule dans le planning et dans les disponibilités.
      </p>
      )}

      {ouvert && (
        <Modale titre="Enregistrer un entretien" onFermer={() => setOuvert(false)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Champ label="Véhicule">
              <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value, km: String(vehicules.find((v) => v._id === e.target.value)?.km ?? '') })}>
                <option value="">Choisir…</option>
                {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
              </select>
            </Champ>
            <div className="grille g2">
              <Champ label="Intervention">
                <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                  {['Vidange', 'Pneus', 'Plaquettes', 'Révision', 'Panne', 'Carrosserie', 'Autre'].map((t) => <option key={t}>{t}</option>)}
                </select>
              </Champ>
              <Champ label="Date"><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Champ>
            </div>
            <div className="grille g2">
              <Champ label="Kilométrage"><input type="number" value={f.km} onChange={(e) => setF({ ...f, km: e.target.value })} /></Champ>
              <Champ label="Coût (DZD)"><input type="number" value={f.cout} onChange={(e) => setF({ ...f, cout: e.target.value })} /></Champ>
            </div>
            <Champ label="Prestataire"><input value={f.prestataire} onChange={(e) => setF({ ...f, prestataire: e.target.value })} /></Champ>
            <div className="grille g2">
              <Champ label="Immobilisé du"><input type="date" value={f.immoDebut} onChange={(e) => setF({ ...f, immoDebut: e.target.value })} /></Champ>
              <Champ label="au"><input type="date" value={f.immoFin} onChange={(e) => setF({ ...f, immoFin: e.target.value })} /></Champ>
            </div>
            <Champ label="Prochaine échéance (km)" aide="Le logiciel vous alertera 2 000 km avant.">
              <input type="number" value={f.prochainKm} onChange={(e) => setF({ ...f, prochainKm: e.target.value })} />
            </Champ>
            <Champ label="Description"><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>Annuler</button>
            <button className="btn btn-p" disabled={!f.vehicle} onClick={() => void creer()}>Enregistrer</button>
          </div>
        </Modale>
      )}
    </>
  );
}
